import React from "react";
import type { Deal } from "@/lib/types/database/deals";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import { getEntityDetailConfig } from "@/lib/entity-registry";
import { DealDetailTabComponents } from "@/features/deals/components/DealDetailTabComponents";
import { formatCurrency } from "@wtc/ui/lib/currency";

// Deal tab components also accept an optional `onTagsUpdate` and `onInlineEdit`
// (the Details tab, which edits DB-backed fields in place).
type DealTabProps = TabContentProps<Deal> & {
  onTagsUpdate?: (newTags: string[]) => Promise<void>;
  onInlineEdit?: (deal: Deal) => Promise<void>;
};

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in the detail config can never silently fall through.
const DEAL_TAB_COMPONENTS = DealDetailTabComponents as Record<
  string,
  React.ComponentType<DealTabProps>
>;

function resolveDealTabComponent(
  tab: TabConfig<Deal> | undefined
): React.ComponentType<DealTabProps> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && DEAL_TAB_COMPONENTS[custom]) {
    return DEAL_TAB_COMPONENTS[custom];
  }
  return DEAL_TAB_COMPONENTS.DealDetailsTab;
}

export interface UseDealDetailModalTabsReturn {
  tabs: TabConfig<Deal>[];
  renderPrimaryArea: (
    activeTab: string,
    deal: Deal,
    onTagsUpdate?: (newTags: string[]) => Promise<void>,
    onInlineEdit?: (deal: Deal) => Promise<void>
  ) => React.ReactNode;
  renderInfoPanel: (deal: Deal) => React.ReactNode;
}

export function useDealDetailModalTabs(): UseDealDetailModalTabsReturn {
  // Get tabs from config (static for now)
  const tabs = getEntityDetailConfig<Deal>("deals").tabs;

  // Render primary area based on active tab. The Details tab owns inline field
  // editing (each DB-backed field is click-to-edit in place), so the deal
  // inline-edit save handler is threaded through to it here.
  const renderPrimaryArea = (
    activeTab: string,
    deal: Deal,
    onTagsUpdate?: (newTags: string[]) => Promise<void>,
    onInlineEdit?: (deal: Deal) => Promise<void>
  ): React.ReactNode => {
    // Get the tab config for this tab
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];

    const TabComponent = resolveDealTabComponent(tabConfig);
    return (
      <TabComponent
        item={deal}
        tab={tabConfig}
        onTagsUpdate={onTagsUpdate}
        onInlineEdit={onInlineEdit}
      />
    );
  };

  // Render info panel (stable across tabs). Read-only glance — inline editing of
  // deal fields lives in the Details tab primary area, so the sidebar stays a
  // read-only summary (no second, competing edit surface).
  const renderInfoPanel = (deal: Deal): React.ReactNode => {
    return (
      <div className="space-y-4">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Deal Information</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Title:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{deal.title}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Stage:</span>
              <p className="text-gray-900 dark:text-gray-100 capitalize">{deal.stage}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {formatCurrency(deal.revenue, { currency: deal.currency, fallback: "", minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Probability:</span>
              <p className="text-gray-900 dark:text-gray-100">{deal.close_probability}%</p>
            </div>
            {deal.close_date && (
              <div>
                <span className="text-gray-600 dark:text-gray-400">Close Date:</span>
                <p className="text-gray-900 dark:text-gray-100">{new Date(deal.close_date).toLocaleDateString()}</p>
              </div>
            )}
            {deal.margin_percentage !== undefined && (
              <div>
                <span className="text-gray-600 dark:text-gray-400">Margin:</span>
                <p className="text-gray-900 dark:text-gray-100">{deal.margin_percentage.toFixed(1)}%</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // NOTE: deals intentionally DO NOT supply `renderInlineInfoPanel`. Omitting it
  // lets `EntityDetailModalLayout` fall through to the shared, config-driven
  // `InlineEditableInfoPanel` — the same grouped, collapsible, click-to-edit
  // properties panel every other entity (contacts, companies, …) uses. Deal
  // fields carry `mobileDetailSectionId`s (ENHANCED_DEALS_CONFIG), so the panel
  // renders progressive-disclosure sections (Overview, Company, Financial,
  // People, …). `renderInfoPanel` above stays as the READ-ONLY fallback the
  // layout uses when inline edit is unavailable (e.g. client users).
  return {
    tabs,
    renderPrimaryArea,
    renderInfoPanel,
  };
}

